import { observer } from 'mobx-react-lite';
import { useParticipant } from 'core';
import { selectLocalParticipant } from 'state/selectors';
import { useVideoStatistic, VideoInfo } from 'features/VideoStatistic';
import { ModerationActionsMenu } from 'features/Moderation/components/ModerationActionsMenu/ModerationActionsMenu';
import { RaiseHandIcon } from 'features/RaiseHand';
import { useRaiseHand } from 'features/RaiseHand/hooks/useRaiseHand';
import { AudioBars } from 'ui/components/AudioBars/AudioBars';
import { isFeatureOn } from 'useCase/featureFlags/featureFlagsApi';
import { FEATURES } from 'consts';
import { ParticipantItemProps } from './types';
import { ParticipantItemContainer, TileIconsContainer, SpeakerIndicator } from './view';

export const ParticipantItem = observer((props: ParticipantItemProps) => {
    const {
        participant,
        className,
        height,
        width,
        onClick,
        PresenterComponent,
        moderationMenuVariant = 'tile',
        withSpeakerIndicator = false,
        isHideSpeakingBorder,
    } = props;
    const { cameraPublication, isSpeaking, screenSharePublication, isAudioMuted, isCameraOn } =
        useParticipant(participant);
    const { checkIsHandRaised } = useRaiseHand();
    const handRaised = checkIsHandRaised(participant);

    const localParticipant = selectLocalParticipant();
    const isLocalParticipantSharingScreen = Boolean(localParticipant?.isScreenShareEnabled);

    const isSpeakerAnimationsEnabled = isFeatureOn(FEATURES.VCS_SPEAKER_ANIMATIONS_ENABLED);

    const { onVideoSizeChanged, ...videoInfo } = useVideoStatistic({
        type: 'participant',
        entity: participant,
    });

    const isShowVideo = Boolean(
        cameraPublication?.isSubscribed && cameraPublication?.track && !cameraPublication?.isMuted,
    );

    const isShowPulseAnimation = isSpeakerAnimationsEnabled && isSpeaking;
    const isShowSpeakerIndicator = isSpeakerAnimationsEnabled && isSpeaking && isCameraOn && withSpeakerIndicator;

    return (
        <ParticipantItemContainer
            className={className}
            style={{ width, height }}
            onClick={onClick}
            $isSpeaking={isSpeaking && !isHideSpeakingBorder}
            data-id={participant.identity}
        >
            <TileIconsContainer>
                {handRaised && <RaiseHandIcon size={moderationMenuVariant === 'tile-small' ? 16 : 24} />}
                <ModerationActionsMenu participant={participant} variant={moderationMenuVariant} />
            </TileIconsContainer>
            <VideoInfo {...videoInfo} />
            <PresenterComponent
                {...props}
                isAudioMuted={isAudioMuted}
                isScreenShared={!!screenSharePublication}
                isVideoEnabled={isShowVideo}
                isShowPulseAnimation={isShowPulseAnimation}
                onSizeChanged={onVideoSizeChanged}
            />
            {isShowSpeakerIndicator && (
                <SpeakerIndicator $isSmall={isLocalParticipantSharingScreen}>
                    <AudioBars count={3} color="white" />
                </SpeakerIndicator>
            )}
        </ParticipantItemContainer>
    );
});

ParticipantItem.displayName = 'ParticipantItem';
